import { Link, useLocation } from 'react-router-dom';
import { Mail, MapPin, MessageCircle, ArrowUpRight } from 'lucide-react';
import logo2 from '../assets/logo2.png';

const LINKS = {
  'Syllabuses': [
    { label: 'Local Syllabus',   to: '/local'      },
    { label: 'Cambridge',        to: '/cambridge'  },
    { label: 'Edexcel',          to: '/edexcel'    },
  ],
  'Services': [
    { label: 'Find a Tutor',     to: '/tutors'     },
    { label: 'Private Classes',  to: '/private'    },
    { label: 'Shop',             to: '/shop'       },
    { label: 'Blog',             to: '/blog'       },
  ],
  'Support': [
    { label: 'Help & Support',   to: '/support'    },
    { label: 'Contact Us',       to: '/contact'    },
  ],
};

const CONTACT = [
  { icon: Mail,          text: 'info@smartedunext.com', href: 'mailto:info@smartedunext.com'    },
  { icon: MessageCircle, text: '077 186 6626',           href: 'https://wa.me/94771866626'        },
  { icon: MapPin,        text: 'Colombo, Sri Lanka',     href: null                               },
];

const Footer = () => {
  const location = useLocation();

  if (
    location.pathname.startsWith('/admin') ||
    location.pathname.startsWith('/my-admin')
  ) return null;

  const year = new Date().getFullYear();

  return (
    <footer className="relative border-t border-white/8 bg-darkBg/80 backdrop-blur-md mt-auto">

      {/* subtle top glow */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-2/3 h-px bg-gradient-to-r from-transparent via-accentBlue/40 to-transparent" />

      <div className="max-w-7xl mx-auto px-6 pt-14 pb-8">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-10 mb-12">

          {/* Brand column */}
          <div className="lg:col-span-2">
            <Link to="/" className="flex items-center gap-0 mb-4 group w-fit">
              <img
                src={logo2}
                alt="Smart Edu Next"
                className="h-20 w-auto object-contain translate-y-1 group-hover:scale-105 transition-transform"
              />
              <span className="text-lg font-extrabold tracking-tighter text-white self-center">
                SMART EDU<span className="text-accentBlue"> NEXT</span>
              </span>
            </Link>
            <p className="text-gray-400 text-sm leading-relaxed mb-6 max-w-xs">
              A comprehensive education platform — past papers, resources, expert tutors and live online classes for Local, Cambridge and Edexcel students.
            </p>

            {/* Contact info */}
            <div className="space-y-2.5">
              {CONTACT.map(({ icon: Icon, text, href }) =>
                href ? (
                  <a key={text} href={href} target="_blank" rel="noopener noreferrer"
                    className="flex items-center gap-2.5 text-sm text-gray-400 hover:text-accentBlue transition-colors group w-fit">
                    <Icon size={14} className="text-accentBlue shrink-0" />
                    <span>{text}</span>
                    <ArrowUpRight size={11} className="opacity-0 group-hover:opacity-100 transition-opacity" />
                  </a>
                ) : (
                  <div key={text} className="flex items-center gap-2.5 text-sm text-gray-400">
                    <Icon size={14} className="text-accentBlue shrink-0" />
                    <span>{text}</span>
                  </div>
                )
              )}
            </div>
          </div>

          {/* Nav link groups */}
          {Object.entries(LINKS).map(([group, links]) => (
            <div key={group}>
              <h4 className="text-xs font-bold text-white uppercase tracking-widest mb-5">{group}</h4>
              <ul className="space-y-3">
                {links.map(({ label, to }) => (
                  <li key={to}>
                    <Link to={to} onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                      className="text-sm text-gray-400 hover:text-white transition-colors hover:translate-x-1 inline-flex items-center gap-1 group">
                      <span className="w-0 group-hover:w-2 h-px bg-accentBlue transition-all duration-200 rounded" />
                      {label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Divider */}
        <div className="h-px bg-white/8 mb-6" />

        {/* Bottom bar */}
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-gray-500">
          <p>© {year} Smart Edu Next. All rights reserved.</p>
          <div className="flex items-center gap-5">
            <Link to="/contact" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} className="hover:text-gray-300 transition-colors">Privacy Policy</Link>
            <Link to="/contact" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} className="hover:text-gray-300 transition-colors">Terms of Service</Link>
            <Link to="/support" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} className="hover:text-gray-300 transition-colors">Help</Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
